// frontend/src/utils/useFormState.js
//
// フォームの状態管理とバリデーションを統合したカスタムフック
//

import { useState, useCallback, useMemo } from 'react';

/**
 * フォームの状態管理とバリデーションを統合したカスタムフック
 *
 * @param {object} initialValues - フォームの初期値
 * @param {object} validationRules - バリデーションルール（validationRules.js形式）
 * @returns {object} - フォーム管理に必要な関数とstate
 */
export const useFormState = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  /**
   * 単一フィールドのバリデーション
   */
  const validateField = useCallback((fieldName, value, formData = {}) => {
    const rule = validationRules[fieldName];
    if (!rule) return null;

    // 必須チェック
    if (rule.required) {
      if (value === null || value === undefined) {
        return rule.errorMessages?.required || `${rule.label}は必須です`;
      }

      if (typeof value === 'string' && value.trim().length === 0) {
        return rule.errorMessages?.required || `${rule.label}は必須です`;
      }

      // ファイル型の場合
      if (rule.required && !(value instanceof File) && typeof value === 'object' && !value) {
        return rule.errorMessages?.required || `${rule.label}は必須です`;
      }
    }

    // カスタムバリデーション
    if (rule.validate) {
      const errorKey = rule.validate(value, formData);
      if (errorKey) {
        return rule.errorMessages?.[errorKey] || `${rule.label}が正しくありません`;
      }
    }

    return null;
  }, [validationRules]);

  /**
   * 全フィールドのバリデーション
   */
  const validateAll = useCallback(() => {
    const newErrors = {};
    let hasError = false;

    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, values[fieldName], values);
      if (error) {
        newErrors[fieldName] = error;
        hasError = true;
      }
    });

    setErrors(newErrors);
    return !hasError;
  }, [values, validationRules, validateField]);

  /**
   * フィールドの値を変更
   */
  const handleChange = useCallback((nameOrEvent, value) => {
    let fieldName, fieldValue;

    if (typeof nameOrEvent === 'string') {
      // handleChange('fieldName', 'value') 形式
      fieldName = nameOrEvent;
      fieldValue = value;
    } else if (nameOrEvent?.target) {
      // handleChange(event) 形式
      const target = nameOrEvent.target;
      fieldName = target.name;
      fieldValue = target.type === 'checkbox' ? target.checked : target.value;
    } else {
      return;
    }

    setValues(prev => ({ ...prev, [fieldName]: fieldValue }));

    // 既にエラーがある場合は即座に再検証
    if (errors[fieldName]) {
      const error = validateField(fieldName, fieldValue, { ...values, [fieldName]: fieldValue });
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldName] = error;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });
    }
  }, [errors, values, validateField]);

  /**
   * フィールドからフォーカスが外れた時
   */
  const handleBlur = useCallback((nameOrEvent) => {
    let fieldName;

    if (typeof nameOrEvent === 'string') {
      fieldName = nameOrEvent;
    } else if (nameOrEvent?.target) {
      fieldName = nameOrEvent.target.name;
    } else {
      return;
    }

    setTouched(prev => ({ ...prev, [fieldName]: true }));

    // バリデーション実行
    const error = validateField(fieldName, values[fieldName], values);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fieldName] = error;
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });
  }, [values, validateField]);

  /**
   * フォームの値を一括設定
   */
  const setFormValues = useCallback((newValues) => {
    setValues(newValues);
    setErrors({});
    setTouched({});
  }, []);

  /**
   * フォームをリセット
   */
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  /**
   * エラーをクリア
   */
  const clearErrors = useCallback((fields = null) => {
    if (!fields) {
      setErrors({});
    } else if (Array.isArray(fields)) {
      setErrors(prev => {
        const newErrors = { ...prev };
        fields.forEach(field => delete newErrors[field]);
        return newErrors;
      });
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fields];
        return newErrors;
      });
    }
  }, []);

  /**
   * 送信可能かどうか
   */
  const canSubmit = useMemo(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  /**
   * エラーがあるかどうか
   */
  const hasErrors = useMemo(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  return {
    // State
    values,
    errors,
    touched,
    hasErrors,
    canSubmit,

    // 値の変更
    setValues: setFormValues,
    handleChange,
    handleBlur,

    // バリデーション
    validateField,
    validateAll,

    // ユーティリティ
    reset,
    clearErrors,
  };
};

export default useFormState;
